
import { Component, Prop, Vue } from "vue-property-decorator";
import { IFabricBrand } from "@/models/fabric";

const ADD_NEW = "__add_new__";

/**
 * Manufacturer / Brand / Brand Collection editor. Renders three DB-populated
 * dropdowns (1/3 width each) plus an "Add new…" option that reveals a write-in
 * input beneath the matching column. Mutates the passed `brand` object directly
 * (handles are derived server-side, so only the *.name fields are edited here).
 */
@Component
export default class BrandFields extends Vue {
  @Prop({ required: true }) readonly brand!: IFabricBrand;
  /** Dotted keys of the required fields currently missing — see missingFieldKeys(). */
  @Prop({ default: () => ({}) }) readonly invalid!: Record<string, boolean>;
  @Prop({ default: () => [] }) readonly brandOptions!: string[];
  @Prop({ default: () => [] }) readonly mfrOptions!: string[];
  @Prop({ default: () => [] }) readonly collectionOptions!: string[];

  protected ADD_NEW = ADD_NEW;
  protected addMfr = false;
  protected addBrand = false;
  protected addCollection = false;

  get collectionName(): string {
    return this.brand.brand_collection ? this.brand.brand_collection.name : "";
  }

  private choices(options: string[], current?: string): string[] {
    const set = new Set((options || []).filter(Boolean));
    if (current) set.add(current);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }
  get mfrChoices(): string[] {
    return this.choices(this.mfrOptions, this.brand.mfr && this.brand.mfr.name);
  }
  get brandChoices(): string[] {
    return this.choices(this.brandOptions, this.brand.name);
  }
  get collectionChoices(): string[] {
    return this.choices(this.collectionOptions, this.collectionName);
  }

  protected selectVal(name: string | undefined, adding: boolean): string {
    return adding ? ADD_NEW : name || "";
  }

  protected onSelect(field: "mfr" | "brand" | "collection", e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    const adding = val === ADD_NEW;
    if (field === "mfr") {
      this.addMfr = adding;
      this.setMfrValue(adding ? "" : val);
    } else if (field === "brand") {
      this.addBrand = adding;
      this.setBrandValue(adding ? "" : val);
    } else {
      this.addCollection = adding;
      this.setCollectionValue(adding ? "" : val);
    }
  }

  private setMfrValue(v: string): void {
    if (!this.brand.mfr) this.$set(this.brand, "mfr", { name: "" });
    this.brand.mfr.name = v;
  }
  private setBrandValue(v: string): void {
    this.brand.name = v;
  }
  private setCollectionValue(v: string): void {
    if (v) {
      if (!this.brand.brand_collection) this.$set(this.brand, "brand_collection", { name: v });
      else this.brand.brand_collection.name = v;
    } else if (this.brand.brand_collection) {
      this.brand.brand_collection.name = "";
    }
  }

  protected setMfr(e: Event): void {
    this.setMfrValue((e.target as HTMLInputElement).value);
  }
  protected setBrand(e: Event): void {
    this.setBrandValue((e.target as HTMLInputElement).value);
  }
  protected setCollection(e: Event): void {
    this.setCollectionValue((e.target as HTMLInputElement).value);
  }
}
